<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="lg:w-1/3 w-full">
                <a-input-search
                    data-cy="active-session-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="lg:w-1/3 w-full" v-if="isAssociationList" data-cy="sessions-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    @change="handleCompanyFilterChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in company_list" :key="index" :value="index">
                        {{ value.company_name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="lg:w-1/3 w-full pt-4 lg:pt-0" v-if="isAdminList" data-cy="sessions-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    :filter-option="false"
                    :not-found-content="null"
                    @search="handleFilterSearch"
                    @change="handleAssociationFilterChange"
                    :allowClear="true"
                >
                    <a-select-opt-group
                        label="Company"
                        v-if="
                            company_association_list.some(
                                (value) =>
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                    >
                        <a-select-option
                            v-for="(value, index) in company_association_list"
                            :key="`${index}-company`"
                            :value="`${index}-company`"
                        >
                            {{ value.association_code }} - {{ value.company_name }}
                        </a-select-option>
                    </a-select-opt-group>
                    <a-select-opt-group
                        label="Association"
                        v-if="
                            association_list.some((value) =>
                                value.name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                    >
                        <a-select-option
                            v-for="(value, index) in association_list"
                            :key="`${index}-association`"
                            :value="`${index}-association`"
                        >
                            {{ value.name }}
                        </a-select-option>
                    </a-select-opt-group>
                </a-select>
            </div>
            <div class="pt-4 lg:pt-0 flex justify-end" v-if="recordsSelected">
                <a-dropdown :trigger="['click']" class="ml-4">
                    <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                        {{ $t('active_sessions.table.mass_actions') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onActionButtons">
                            <a-menu-item key="close" data-cy="actions-close-item">
                                {{ $t('active_sessions.table.actions.close') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="active-session-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.user_id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="{ selectedRowKeys: selected_row_keys, onChange: onSelectChange }"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('active_sessions.table.actions.close')">
                                    <a-button
                                        class="text-red-400 hover:text-red-400 focus:text-red-400"
                                        @click="onCloseSession(record.user_id)"
                                    >
                                        <StopOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'last_activity'">{{ timeAgo(text) }}</template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { DownOutlined, StopOutlined } from '@ant-design/icons-vue';

export default {
    components: { DownOutlined, StopOutlined },
    data() {
        return {
            data: [],
            search_value: null,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            current_filter: {},
            sorter: {},
            params: {},
            filter_value: '',
            association_list: [],
            company_list: [],
            company_association_list: [],
            selected_row_keys: [],
        };
    },
    props: {
        company_id: {
            type: String,
            default: '',
        },
        association_id: {
            type: String,
            default: '',
        },
    },
    mounted() {
        this.fetch();

        if (this.isAdminList) {
            this.$http
                .all([
                    this.$http.get('/api/admin/list-companies-by-associations'),
                    this.$http.get('/api/admin/list-associations'),
                ])
                .then(
                    this.$http.spread((companies, associations) => {
                        this.company_association_list = companies.data.data;

                        this.association_list = associations.data.data;
                    }),
                )
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVACL01' }),
                    });
                });
        } else if (this.isAssociationList) {
            this.$http
                .get('/api/admin/list-companies', {
                    params: {
                        association_id: this.$store.getters['user/currentAssociationId'],
                    },
                })
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCL02' }),
                    });
                });
        }
    },
    computed: {
        getColumns() {
            let columns = [
                {
                    key: 'actions',
                    width: 55,
                },
                {
                    title: this.$t('globals.tables.active_sessions.headers.association_code'),
                    key: 'association_code',
                    dataIndex: 'association_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.active_sessions.headers.company_code'),
                    key: 'company_code',
                    dataIndex: 'company_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.active_sessions.headers.ip_address'),
                    key: 'ip_address',
                    dataIndex: 'ip_address',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.active_sessions.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.active_sessions.headers.last_active'),
                    key: 'last_activity',
                    dataIndex: 'last_activity',
                    defaultSortOrder: 'descend',
                    sorter: true,
                },
            ];

            if (this.isAdminList) {
                // do nothing
            } else if (this.isAssociationList) {
                columns.splice(1, 1);
            } else {
                columns.splice(1, 2);
            }

            return columns;
        },
        isCompanyList() {
            return this.company_id != '' && this.company_id != null && this.company_id != undefined;
        },
        isAssociationList() {
            return this.association_id != '' && this.association_id != null && this.association_id != undefined;
        },
        isAdminList() {
            return (
                (this.company_id == '' || this.company_id == null || this.company_id == undefined) &&
                (this.association_id == '' || this.association_id == null || this.association_id == undefined)
            );
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
    },
    methods: {
        timeAgo(datetime) {
            let mjs = this.$dayjs(datetime);

            return mjs.isValid() ? mjs.fromNow() : '';
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = {
                company_id: this.params.company_id ? this.params.company_id : null,
                association_id: this.params.association_id ? this.params.association_id : null,
                search_value: this.params.search_value ? this.params.search_value : null,
                ...params,
            };

            let url = '';

            if (this.isAdminList) {
                url = '/api/active-sessions';
            } else if (this.isAssociationList) {
                url = '/api/associations/' + this.association_id + '/active-sessions';
            } else if (this.isCompanyList) {
                url = '/api/companies/' + this.company_id + '/active-sessions';
            } else {
                this.$router.push({
                    name: 'not-found',
                });
            }

            this.$http
                .get(url, {
                    params: {
                        ...this.params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else if (error.response.status == 401) {
                        window.location.href = '/login';
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVAS01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.pagination.current = 1;
                this.onSelectChange([]);

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleFilterSearch(value) {
            this.filter_value = value;
        },
        filterOption(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        handleCompanyFilterChange(value) {
            if (value == undefined) {
                this.fetch({});
            } else {
                this.pagination.current = 1;
                this.onSelectChange([]);

                this.fetch({
                    company_id: this.company_list[value].company_id,
                });
            }
        },
        handleAssociationFilterChange(value) {
            let company_id = null;
            let association_id = null;

            if (value !== undefined) {
                if (value.includes('company')) {
                    company_id = this.company_association_list[parseInt(value)].company_id;
                    association_id = this.company_association_list[parseInt(value)].association_id;
                } else {
                    association_id = this.association_list[parseInt(value)].id;
                }
            }

            this.pagination.current = 1;
            this.onSelectChange([]);

            this.fetch({
                company_id: company_id,
                association_id: association_id,
            });
        },
        onCloseSession(id = '') {
            let ids = [];

            let title_message = '';
            let content_message = '';

            let that = this;

            if (id !== '' && id !== undefined) {
                ids = [id];
                title_message = this.$t('active_sessions.notifications.close_session.title');
                content_message = this.$t('active_sessions.notifications.close_session.content');
            } else if (this.selected_row_keys.length >= 1) {
                ids = this.selected_row_keys;
                title_message = this.$t('active_sessions.notifications.close_sessions.title');
                content_message = this.$t('active_sessions.notifications.close_sessions.content');
            } else {
                return false;
            }

            this.$confirm({
                title: title_message,
                content: content_message,
                onOk() {
                    that.$http
                        .post('/api/active-sessions/close', { ids: ids })
                        .then((response) => {
                            that.reload();

                            that.$notification.success({
                                message: response.data.message,
                            });
                        })
                        .catch((error) => {
                            that.$notification.error({
                                message: that.$t('globals.global.notifications.error.any', { code: 'CVCAS01' }),
                            });
                        });
                },
            });
        },
        onSelectChange(selected_row_keys, selected_rows) {
            this.selected_row_keys = selected_row_keys;
            this.$emit('onSelectChange', selected_row_keys);
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'close':
                    this.onCloseSession();
                    break;
            }
        },
    },
};
</script>
